import React, { useEffect, useRef } from 'react';

interface GlitterWarpProps {
  particleCount?: number;
  speed?: number;
  starColor?: string;
  glowColor?: string;
  className?: string;
}

export const GlitterWarp: React.FC<GlitterWarpProps> = ({
  particleCount = 500,
  speed = 1.5,
  starColor = '#ffffff',
  glowColor = '#0070f3',
  className = '',
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationFrameId: number;
    let width = (canvas.width = window.innerWidth);
    let height = (canvas.height = window.innerHeight);

    const handleResize = () => {
      if (!canvas) return;
      width = canvas.width = window.innerWidth;
      height = canvas.height = window.innerHeight;
    };

    window.addEventListener('resize', handleResize);

    const stars: Array<{
      x: number;
      y: number;
      z: number;
      prevZ: number;
      size: number;
      glitterPhase: number;
      glitterSpeed: number;
    }> = [];

    const maxDepth = 1500;

    for (let i = 0; i < particleCount; i++) {
      stars.push({
        x: (Math.random() - 0.5) * width * 2,
        y: (Math.random() - 0.5) * height * 2,
        z: Math.random() * maxDepth,
        prevZ: maxDepth,
        size: Math.random() * 1.8 + 0.6,
        glitterPhase: Math.random() * Math.PI * 2,
        glitterSpeed: 0.03 + Math.random() * 0.05,
      });
    }

    const cx = width / 2;
    const cy = height / 2;

    const render = () => {
      ctx.fillStyle = 'rgba(5, 10, 20, 0.45)';
      ctx.fillRect(0, 0, width, height);

      const fov = 400;

      for (let i = 0; i < stars.length; i++) {
        const star = stars[i];

        star.prevZ = star.z;
        star.z -= speed * 4;
        star.glitterPhase += star.glitterSpeed;

        if (star.z <= 1) {
          star.z = maxDepth;
          star.prevZ = maxDepth;
          star.x = (Math.random() - 0.5) * width * 2;
          star.y = (Math.random() - 0.5) * height * 2;
        }

        const k = fov / star.z;
        const px = star.x * k + cx;
        const py = star.y * k + cy;

        const prevK = fov / star.prevZ;
        const prevPx = star.x * prevK + cx;
        const prevPy = star.y * prevK + cy;

        if (px >= 0 && px <= width && py >= 0 && py <= height) {
          const twinkle = (Math.sin(star.glitterPhase) + 1) / 2;
          const alpha = Math.min(1, (1 - star.z / maxDepth) * (0.4 + twinkle * 0.6));
          const currentSize = Math.max(0.8, star.size * (1 - star.z / maxDepth) * 2.2);

          ctx.beginPath();
          ctx.strokeStyle = `rgba(0, 112, 243, ${alpha * 0.5})`;
          ctx.lineWidth = currentSize * 0.8;
          ctx.moveTo(prevPx, prevPy);
          ctx.lineTo(px, py);
          ctx.stroke();

          ctx.beginPath();
          ctx.arc(px, py, currentSize, 0, Math.PI * 2);
          ctx.fillStyle = twinkle > 0.8 ? '#ffffff' : starColor;
          ctx.shadowBlur = twinkle > 0.7 ? 12 : 4;
          ctx.shadowColor = glowColor;
          ctx.fill();

          ctx.shadowBlur = 0;
        }
      }

      animationFrameId = requestAnimationFrame(render);
    };

    render();

    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationFrameId);
    };
  }, [particleCount, speed, starColor, glowColor]);

  return (
    <canvas
      ref={canvasRef}
      className={`fixed inset-0 pointer-events-none z-0 ${className}`}
      style={{ background: 'radial-gradient(ellipse at center, #0a192f 0%, #030712 100%)' }}
    />
  );
};

export default GlitterWarp;